import { useEffect, useState } from "react";
import { Badge, Button, Modal } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

function EraTestCaseModal({ show, onClose, testCase }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const getInterfaceBadgeClass = (value) => {
    const normalizedValue = String(value ?? "").toUpperCase();

    if (normalizedValue === "BTM") {
      return "bg-primary-subtle text-primary-emphasis border border-primary-subtle";
    }

    if (normalizedValue === "JRU") {
      return "bg-warning-subtle text-warning-emphasis border border-warning-subtle";
    }

    if (normalizedValue === "RTM") {
      return "bg-success-subtle text-success-emphasis border border-success-subtle";
    }

    if (normalizedValue === "DMI") {
      return "bg-info-subtle text-info-emphasis border border-info-subtle";
    }

    return "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle";
  };

  useEffect(() => {
    if (!show || !testCase?.id) {
      setDetails(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setLoading(true);

      try {
        const res = await fetchWithAuth(`/api/era-test-cases/${testCase.id}/details`);

        if (!res.ok) {
          if (!cancelled) {
            setDetails(null);
          }
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          setDetails(data);
        }
      } catch (err) {
        if (!cancelled) {
          setDetails(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [show, testCase]);

  if (!testCase) return null;

  const steps = details?.steps ?? [];

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          ERA Test Case {details?.tc_number ?? testCase.tc_number}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <table className="table mb-4">
              <tbody>
                <tr>
                  <th style={{ width: "160px" }}>Feature</th>
                  <td>
                    <div className="fw-semibold">{details?.ft_number ?? testCase.ft_number}</div>
                    {details?.ft_title && (
                      <div className="text-muted small mt-1">{details.ft_title}</div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Title</th>
                  <td>{details?.tc_title ?? testCase.tc_title}</td>
                </tr>
              </tbody>
            </table>

            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.86rem" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "70px" }}>Step</th>
                    <th>Description</th>
                    <th style={{ width: "105px" }}>Interface</th>
                    <th style={{ width: "80px" }}>I/O</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-muted text-center py-3">
                        No steps found.
                      </td>
                    </tr>
                  ) : steps.map((step, index) => {
                    const isInput = String(step.st_io ?? "").toUpperCase() === "I";
                    const ioLabel = isInput ? "IN" : String(step.st_io ?? "").toUpperCase() === "O" ? "OUT" : String(step.st_io ?? "");

                    return (
                      <tr
                        key={step.id}
                        style={{
                          "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
                        }}
                      >
                        <td>{step.step_no}</td>
                        <td>{step.st_description}</td>
                        <td>
                          {step.st_interface && (
                            <Badge className={getInterfaceBadgeClass(step.st_interface)}>
                              {step.st_interface}
                            </Badge>
                          )}
                        </td>
                        <td>
                          {ioLabel && (
                            <Badge bg={isInput ? "primary" : "success"}>
                              {ioLabel}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EraTestCaseModal;
